import { useNavigate } from "react-router"
import type { NavigateOptions } from "react-router"

import { useInsightsWindow } from "./use-insights-window"

/**
 * In-module navigation that carries the display window forward.
 *
 * The selected window is held in the URL search params (see
 * `useInsightsWindow`), so a bare `navigate("actions")` writes a location
 * with an EMPTY search and silently resets every page it lands on back to
 * the trailing-12-month default. Every navigation between `/neat`, the
 * actions aggregator, the E2 detail and the variance page goes through this
 * hook instead, so one selection holds across the whole flow.
 *
 * Only the window params ride along — page-local state (sensitivity, filters)
 * stays on the page that owns it.
 */
export const useInsightsNavigate = (): ((
  to: string,
  options?: NavigateOptions
) => void) => {
  const navigate = useNavigate()
  const { window, start, end } = useInsightsWindow()

  return (to: string, options?: NavigateOptions): void => {
    const params = new URLSearchParams()
    if (window != null) {
      params.set("window", window)
    }
    if (start != null) {
      params.set("start", start)
    }
    if (end != null) {
      params.set("end", end)
    }
    const search = params.toString()
    void navigate(
      { pathname: to, search: search === "" ? "" : `?${search}` },
      options
    )
  }
}
